import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Drawer,
  Flex,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Input,
  VStack,
  useDisclosure,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  FormControl,
  FormLabel,
  Spinner,
  useToast,
  Textarea,
  Badge,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  AddInventorySource,
  GetInventorySources,
  ApproveInventorySource,
} from "../../../../InventoryServices/InventorySourceAPI/InventorySource";
import ApprovalModal from "../ApprovalModal";
import MediaPreviewModal from "../MediaPreviewModal";

// Maps API approval_status value to Badge colorScheme + display label
const STATUS_META = {
  pending: { label: "Pending", colorScheme: "yellow" },
  approved: { label: "Approved", colorScheme: "green" },
  declined: { label: "Rejected", colorScheme: "red" },
  rejected: { label: "Rejected", colorScheme: "red" },
};

const getStatusMeta = (status) =>
  STATUS_META[status] || { label: status || "Pending", colorScheme: "yellow" };

const SourceSection = ({ versionId, onApprovalStatusChange }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [formValues, setFormValues] = useState({});
  const [file, setFile] = useState(null);
  const [sourceData, setSourceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Approval modal state
  const {
    isOpen: isApprovalOpen,
    onOpen: onApprovalOpen,
    onClose: onApprovalClose,
  } = useDisclosure();
  const [selectedSource, setSelectedSource] = useState(null);
  const [approving, setApproving] = useState(false);

  // Preview modal state
  const {
    isOpen: isPreviewOpen,
    onOpen: onPreviewOpen,
    onClose: onPreviewClose,
  } = useDisclosure();
  const [previewFile, setPreviewFile] = useState(null);

  const headerBg = useColorModeValue("blue.500", "blue.600");
  const headerColor = useColorModeValue("white", "gray.100");
  const rowHoverBg = useColorModeValue("blue.50", "gray.700");
  const tableBg = useColorModeValue("white", "gray.800");

  // Fetch sources
  const fetchSources = useCallback(async () => {
    if (!versionId) return;
    setLoading(true);
    try {
      const sources = await GetInventorySources(versionId);
      setSourceData(sources);
    } catch (err) {
      console.error("Error fetching inventory sources:", err);
      toast({
        title: "Failed to load sources",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [versionId, toast]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  // Let parent (VariantTab) know whether at least one source is approved,
  // so it can enable/disable "+ Add Output" accordingly.
  useEffect(() => {
    if (typeof onApprovalStatusChange === "function") {
      const hasApproved = (sourceData || []).some(
        (src) => src.approval_status === "approved"
      );
      onApprovalStatusChange(hasApproved);
    }
  }, [sourceData, onApprovalStatusChange]);

  const handleTypeSelect = (type) => {
    setFormValues((prev) => ({ ...prev, type }));
  };

  const handleAdd = async () => {
    if (!versionId) {
      toast({
        title: "Version not found",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);

      const payload = new FormData();

      let apiType = "link";
      if (formValues.type === "Git") apiType = "giturl";
      else if (formValues.type === "Link") apiType = "link";
      else if (formValues.type === "Upload") apiType = "upload";

      payload.append("type", apiType);

      if (formValues.name) {
        payload.append("name", formValues.name);
      }
      if (formValues.comments) {
        payload.append("comments", formValues.comments);
      }

      if (apiType === "upload") {
        if (!file) {
          toast({
            title: "File required",
            description: "Please select a file to upload.",
            status: "warning",
            duration: 2500,
            isClosable: true,
          });
          setLoading(false);
          return;
        }
        payload.append("media", file);
      } else if (apiType === "link") {
        if (!formValues.value) {
          toast({
            title: "URL required",
            description: "Please enter a valid link.",
            status: "warning",
            duration: 2500,
            isClosable: true,
          });
          setLoading(false);
          return;
        }
        payload.append("link", formValues.value);
      } else if (apiType === "giturl") {
        if (!formValues.value) {
          toast({
            title: "Git URL required",
            description: "Please enter a valid Git URL.",
            status: "warning",
            duration: 2500,
            isClosable: true,
          });
          setLoading(false);
          return;
        }
        payload.append("git_url", formValues.value);
      }

      await AddInventorySource(versionId, payload);

      setFormValues({});
      setFile(null);
      onClose();

      toast({
        title: "Source added successfully!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      await fetchSources();
    } catch (err) {
      console.error("Error adding source:", err);
      toast({
        title: "Failed to add source",
        description: err?.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

  // Clicking the source name behaves differently based on its type:
  // - "upload" -> uploaded media, show it in the preview modal
  // - "link" / "giturl" -> external URL, open it directly in a new tab
  const handleSourceClick = (src) => {
    const url = src.file_path;
    if (!url) return;

    if (src.type === "upload") {
      const ext = (src.extension || "").toLowerCase();
      const isImage =
        (src.file_type || "").toLowerCase().startsWith("image") ||
        IMAGE_EXTENSIONS.includes(ext) ||
        !ext;
      setPreviewFile({
        url,
        title: src.original_name || src.name || "Source",
        isImage,
      });
      onPreviewOpen();
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // Open the approval modal for a specific source row
  const handleOpenApproval = (src) => {
    setSelectedSource(src);
    onApprovalOpen();
  };

  // Submit approve/reject decision for the selected source
  const handleApprovalSubmit = async (approval_status, approval_comment) => {
    if (!selectedSource || !versionId) return;
    setApproving(true);
    try {
      await ApproveInventorySource(versionId, selectedSource.id, {
        approval_status,
        approval_comment,
      });
      toast({
        title:
          approval_status === "approved" ? "Source approved" : "Source rejected",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onApprovalClose();
      setSelectedSource(null);
      await fetchSources();
    } catch (err) {
      console.error("Error updating approval status:", err);
      toast({
        title: "Failed to update status",
        description: err?.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setApproving(false);
    }
  };

  const displayType = formValues.type || "Select Type";

  return (
    <Box
      bg={tableBg}
      p={6}
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
      shadow="sm"
      w="100%"
      minW={0}
    >
      <Flex
        justify="space-between"
        align={{ base: "flex-start", sm: "center" }}
        direction={{ base: "column", sm: "row" }}
        gap={3}
        mb={4}
      >
        <Text fontSize="lg" fontWeight="semibold" color="black">
          Inventory Sources
        </Text>
        <Button
          colorScheme="blue"
          size="sm"
          onClick={onOpen}
          flexShrink={0}
          w={{ base: "100%", sm: "auto" }}
        >
          + Add Source
        </Button>
      </Flex>

      {loading && sourceData.length === 0 ? (
        <Flex justify="center" py={8}>
          <Spinner size="lg" color="blue.500" />
        </Flex>
      ) : (
        <TableContainer
          overflowX="auto"
          shadow="sm"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          w="100%"
          maxW="100%"
        >
          <Table variant="simple" size="md">
            <Thead bg={headerBg}>
              <Tr>
                <Th color={headerColor} fontSize="sm">
                  #
                </Th>
                <Th color={headerColor} fontSize="sm">
                  Name
                </Th>
                <Th color={headerColor} fontSize="sm">
                  Created At
                </Th>
                <Th color={headerColor} fontSize="sm">
                  Updated At
                </Th>
                <Th color={headerColor} fontSize="sm">
                  Status
                </Th>
                <Th color={headerColor} fontSize="sm">
                  Action
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {sourceData?.length > 0 ? (
                sourceData.map((src, idx) => {
                  const statusMeta = getStatusMeta(src.approval_status);
                  return (
                    <Tr
                      key={src.id || idx}
                      _hover={{
                        bg: rowHoverBg,
                        transform: "scale(1.01)",
                        boxShadow: "md",
                        transition: "all 0.2s",
                        cursor: "pointer",
                      }}
                    >
                      <Td>{idx + 1}</Td>
                      <Td fontWeight="600">
                        <Button
                          variant="link"
                          color="blue.500"
                          onClick={() => handleSourceClick(src)}
                        >
                          {src.original_name || src.name || `Source ${idx + 1}`}
                        </Button>
                      </Td>
                      <Td fontSize="sm" color="gray.600">
                        {src.created_at
                          ? new Date(src.created_at).toLocaleString()
                          : "-"}
                      </Td>
                      <Td fontSize="sm" color="gray.600">
                        {src.updated_at
                          ? new Date(src.updated_at).toLocaleString()
                          : "-"}
                      </Td>
                      <Td>
                        <Badge colorScheme={statusMeta.colorScheme}>
                          {statusMeta.label}
                        </Badge>
                      </Td>
                      <Td>
                        {src.approval_status !== "approved" ? (
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => handleOpenApproval(src)}
                          >
                            Action
                          </Button>
                        ) : (
                          "-"
                        )}
                      </Td>
                    </Tr>
                  );
                })
              ) : (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={8} color="gray.500">
                    No sources available for this version
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>Add Source</DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Source Name
                </FormLabel>
                <Input
                  placeholder="Enter Source"
                  value={formValues.name || ""}
                  onChange={(e) =>
                    setFormValues({ ...formValues, name: e.target.value })
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Source Type
                </FormLabel>
                <Menu autoSelect={false} closeOnSelect={true}>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    textAlign="left"
                    borderRadius="lg"
                    bg="gray.50"
                    borderColor="gray.200"
                    borderWidth="1px"
                    _hover={{ bg: "gray.100" }}
                    _expanded={{ bg: "gray.100" }}
                    _focus={{ boxShadow: "outline" }}
                    width="100%"
                  >
                    {displayType}
                  </MenuButton>
                  <MenuList
                    zIndex="1000"
                    maxHeight="200px"
                    overflowY="auto"
                    borderRadius="lg"
                    borderColor="gray.200"
                    boxShadow="lg"
                  >
                    <MenuItem onClick={() => handleTypeSelect("Upload")}>
                      Upload
                    </MenuItem>
                    <MenuItem onClick={() => handleTypeSelect("Link")}>
                      Link
                    </MenuItem>
                  </MenuList>
                </Menu>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  {formValues.type === "Upload" ? "Upload File" : "Source URL"}
                </FormLabel>
                {formValues.type === "Upload" ? (
                  <Input
                    type="file"
                    onChange={(e) => setFile(e.target.files[0])}
                  />
                ) : (
                  <Input
                    placeholder="Enter URL"
                    value={formValues.value || ""}
                    onChange={(e) =>
                      setFormValues({ ...formValues, value: e.target.value })
                    }
                  />
                )}
              </FormControl>

              {/* Comment Box */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Comments
                </FormLabel>
                <Textarea
                  placeholder="Enter comments"
                  value={formValues.comments || ""}
                  onChange={(e) =>
                    setFormValues({ ...formValues, comments: e.target.value })
                  }
                />
              </FormControl>
            </VStack>
          </DrawerBody>

          <DrawerFooter>
            <Button
              variant="outline"
              mr={3}
              onClick={onClose}
              isDisabled={loading}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleAdd}
              isLoading={loading}
              loadingText="Saving..."
            >
              Save
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Approve / Reject Modal */}
      <ApprovalModal
        isOpen={isApprovalOpen}
        onClose={() => {
          if (!approving) {
            onApprovalClose();
            setSelectedSource(null);
          }
        }}
        itemName={
          selectedSource
            ? selectedSource.original_name ||
              selectedSource.name ||
              "Source"
            : ""
        }
        loading={approving}
        onSubmit={handleApprovalSubmit}
      />

      {/* File Preview Modal */}
      <MediaPreviewModal
        isOpen={isPreviewOpen}
        onClose={onPreviewClose}
        title={previewFile?.title}
        url={previewFile?.url}
        isImage={previewFile?.isImage}
      />
    </Box>
  );
};

export default SourceSection;